import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, FileText, Download, Trash2, ExternalLink, ClipboardList, ChevronRight, Loader2, Images, X, Eye, FileAudio, Receipt, ReceiptText, Send } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  ResponsiveDialog as Dialog,
  ResponsiveDialogContent as DialogContent,
  ResponsiveDialogDescription as DialogDescription,
  ResponsiveDialogFooter as DialogFooter,
  ResponsiveDialogHeader as DialogHeader,
  ResponsiveDialogTitle as DialogTitle,
  ResponsiveDialogBody as DialogBody,
} from "@/components/ui/responsive-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { ReportTypeBadge, InvoiceBadge, EmailSentBadge } from "@/components/Badges";
import { InvoicePanel } from "@/components/report/InvoicePanel";
import { EmailHistory } from "@/components/report/EmailHistory";
import { SendReportDialog } from "@/components/report/SendReportDialog";
import { DateRangePicker } from "@/components/DatePicker";
import { formatDate, num } from "@/lib/format";
import { downloadReportPdf, openReportPdf } from "@/lib/pdf";
import { readM365Callback, stripM365Params, toastM365Callback } from "@/lib/m365";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";
import { imageCaption, imageSrc, openImage, fileUrl } from "@/lib/files";
import { formatClock } from "@/hooks/useAudioRecorder";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const chip = "h-9 shrink-0 gap-1.5 rounded-full border px-3 text-xs data-[state=on]:border-transparent data-[state=on]:bg-[#0B0D10] data-[state=on]:text-white dark:data-[state=on]:bg-white dark:data-[state=on]:text-[#0B0D10]";

const Row = ({ label, value }) => value ? (
  <div className="py-2 text-sm">
    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="mt-0.5 whitespace-pre-wrap">{value}</div>
  </div>
) : null;

export default function Rapportar() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [invoice, setInvoice] = useState(params.get("fakturert") === "0" ? "open" : params.get("fakturert") === "1" ? "invoiced" : "all"); // all | open | invoiced
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [previewReport, setPreviewReport] = useState(null);
  const [sendFor, setSendFor] = useState(null);      // rapport som skal sendast til kunde (SendReportDialog)
  const [emailRefresh, setEmailRefresh] = useState(0); // bump → EmailHistory hentar lista på nytt

  const load = () => {
    setLoading(true);
    api.get("/reports").then((r) => setReports(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  // deep link ?open=<id>  (+ ?m365=connected|error når brukaren kjem tilbake frå Microsoft)
  useEffect(() => {
    const id = params.get("open");
    const cb = readM365Callback(params);
    if (!id && !cb) return;
    if (cb) toastM365Callback(cb);
    if (id) {
      setDetailLoading(true);
      api.get(`/reports/${id}`)
        .then((r) => {
          setDetail(r.data);
          // Kom tilbake frå Outlook-kopling med denne rapporten open → gå rett til sending
          if (cb?.status === "connected") setSendFor(r.data);
        })
        .catch(() => toast.error("Fann ikkje rapporten."))
        .finally(() => setDetailLoading(false));
    }
    const p = stripM365Params(params);
    p.delete("open");
    setParams(p, { replace: true });
  }, [params, setParams]);

  const filtered = useMemo(() => {
    const t = q.toLowerCase().trim();
    return reports.filter((r) => {
      if (type !== "all" && (r.report_type || "service") !== type) return false;
      if (invoice === "open" && r.invoiced) return false;
      if (invoice === "invoiced" && !r.invoiced) return false;
      if (from && r.date < from) return false;
      if (to && r.date > to) return false;
      if (!t) return true;
      const ms = r.machine_snapshot || {};
      const cs = r.customer_snapshot || {};
      return [r.report_number, r.order_number, r.technician, r.service_type, r.title, ms.brand, ms.type, ms.internal_number, cs.name].filter(Boolean).join(" ").toLowerCase().includes(t);
    });
  }, [reports, q, type, invoice, from, to]);

  const counts = useMemo(() => ({
    all: reports.length,
    service: reports.filter((r) => (r.report_type || "service") === "service").length,
    jobb: reports.filter((r) => r.report_type === "jobb").length,
    open: reports.filter((r) => !r.invoiced).length,
  }), [reports]);

  /** Apply a PATCHed report to both the open detail and the list (no reload needed). */
  const applyUpdated = (r) => {
    setDetail((d) => (d && d.id === r.id ? { ...d, ...r } : d));
    setReports((list) => list.map((x) => (x.id === r.id ? { ...x, ...r, images: undefined } : x)));
  };

  /** After an e-mail was sent: bump counters on the detail + list row, refresh history. */
  const onEmailSent = (entry) => {
    const bump = (r) => ({
      ...r,
      email_sent_count: (r.email_sent_count || 0) + 1,
      last_email_sent_at: entry?.sent_at,
      last_email_to: entry?.to,
    });
    setDetail((d) => (d && d.id === entry?.report_id ? bump(d) : d));
    setReports((list) => list.map((x) => (x.id === entry?.report_id ? bump(x) : x)));
    setEmailRefresh((n) => n + 1);
  };

  const openDetail = async (r) => {
    setDetailLoading(true);
    try {
      const res = await api.get(`/reports/${r.id}`);
      setDetail(res.data);
    } catch (e) {
      toast.error("Kunne ikkje hente rapporten.");
    } finally {
      setDetailLoading(false);
    }
  };
  const dl = async (r) => { try { await downloadReportPdf(r); } catch (e) { toast.error("Kunne ikkje laste ned PDF."); } };
  const openPdf = async (r) => { try { await openReportPdf(r); } catch (e) { toast.error("Kunne ikkje opne PDF."); } };
  const doDelete = async () => {
    try {
      await api.delete(`/reports/${toDelete.id}`);
      toast.success("Rapport sletta.");
      setToDelete(null);
      setDetail(null);
      load();
    } catch (e) { toast.error("Kunne ikkje slette."); setToDelete(null); }
  };

  const titleOf = (r) => r.title || r.service_type || (r.report_type === "jobb" ? "Jobbrapport" : "Servicerapport");
  const machineOf = (r) => [r.machine_snapshot?.brand, r.machine_snapshot?.type].filter(Boolean).join(" ") + (r.machine_snapshot?.internal_number ? ` (#${r.machine_snapshot.internal_number})` : "");
  const hasFilters = q || type !== "all" || invoice !== "all" || from || to;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Rapportar"
        description="Alle service- og jobbrapportar. Opne som PDF eller last ned for å sende til kunden."
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button data-testid="new-report-button"><Plus /> Ny rapport</Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem className="cursor-pointer py-2.5" onClick={() => navigate("/rapportar/ny-service")} data-testid="new-report-service-item"><FileText className="mr-2 h-4 w-4" /> Servicerapport</DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer py-2.5" onClick={() => navigate("/rapportar/ny-jobb")} data-testid="new-report-jobb-item"><ClipboardList className="mr-2 h-4 w-4" /> Jobbrapport</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative w-full lg:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="report-search-input" placeholder="Søk rapportnr, ordrenr, maskin, kunde …" value={q} onChange={(e) => setQ(e.target.value)} className="h-11 bg-card pl-9" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ToggleGroup type="single" value={type} onValueChange={(v) => v && setType(v)} className="justify-start gap-1.5" data-testid="report-type-filter">
            <ToggleGroupItem value="all" className={chip} data-testid="report-filter-all">Alle <span className="rounded-full bg-black/10 px-1.5 tabular-nums dark:bg-white/20">{counts.all}</span></ToggleGroupItem>
            <ToggleGroupItem value="service" className={chip} data-testid="report-filter-service"><FileText className="h-3.5 w-3.5" /> Service <span className="rounded-full bg-black/10 px-1.5 tabular-nums dark:bg-white/20">{counts.service}</span></ToggleGroupItem>
            <ToggleGroupItem value="jobb" className={chip} data-testid="report-filter-jobb"><ClipboardList className="h-3.5 w-3.5" /> Jobb <span className="rounded-full bg-black/10 px-1.5 tabular-nums dark:bg-white/20">{counts.jobb}</span></ToggleGroupItem>
          </ToggleGroup>
          <ToggleGroup type="single" value={invoice} onValueChange={(v) => setInvoice(v || "all")} className="justify-start gap-1.5" data-testid="report-invoice-filter">
            <ToggleGroupItem value="open" className={chip} data-testid="report-filter-open"><Receipt className="h-3.5 w-3.5" /> Ikkje fakturert <span className="rounded-full bg-black/10 px-1.5 tabular-nums dark:bg-white/20">{counts.open}</span></ToggleGroupItem>
            <ToggleGroupItem value="invoiced" className={chip} data-testid="report-filter-invoiced"><ReceiptText className="h-3.5 w-3.5" /> Fakturert</ToggleGroupItem>
          </ToggleGroup>
          <DateRangePicker
            from={from}
            to={to}
            onChange={({ from: f, to: t }) => { setFrom(f); setTo(t); }}
            placeholder="Periode"
            className="h-10 w-full sm:w-auto sm:min-w-[220px] md:h-9 md:text-xs"
            testid="report-date-range"
          />
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={() => { setQ(""); setType("all"); setInvoice("all"); setFrom(""); setTo(""); }} data-testid="report-clear-filters"><X /> Nullstill</Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-lg" />)}</div>
      ) : filtered.length === 0 ? (
        <Card className="shadow-sm">
          <EmptyState className="m-4 border-0" icon={FileText} title={reports.length === 0 ? "Ingen rapportar enno" : "Ingen treff"}
            description={reports.length === 0 ? "Lag ein servicerapport eller jobbrapport etter utført arbeid." : "Prøv eit anna søk, type eller periode."}
            action={reports.length === 0 && <div className="flex gap-2"><Button onClick={() => navigate("/rapportar/ny-service")}><Plus /> Servicerapport</Button><Button variant="outline" onClick={() => navigate("/rapportar/ny-jobb")}><Plus /> Jobbrapport</Button></div>} />
        </Card>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="space-y-2 md:hidden" data-testid="report-card-list">
            {filtered.map((r) => (
              <button key={r.id} type="button" onClick={() => openDetail(r)} data-testid={`report-card-${r.id}`} className="pressable flex w-full items-center gap-3 rounded-lg border bg-card p-3 text-left shadow-sm">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{r.report_number}</span>
                    <ReportTypeBadge type={r.report_type} />
                    {r.invoiced && <InvoiceBadge invoiced compact />}
                    <EmailSentBadge count={r.email_sent_count} compact />
                  </div>
                  <div className="mt-0.5 truncate text-sm">{titleOf(r)}</div>
                  <div className="truncate text-xs text-muted-foreground">{formatDate(r.date)} · {r.customer_snapshot?.name || "—"}{machineOf(r) ? ` · ${machineOf(r)}` : ""}{r.order_number ? ` · Ordre ${r.order_number}` : ""}</div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
            ))}
          </div>

          {/* Desktop table */}
          <Card className="hidden overflow-hidden shadow-sm md:block">
            <Table data-testid="report-list-table">
              <TableHeader className="bg-muted/50">
                <TableRow>
                  <TableHead>Rapport</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Dato</TableHead>
                  <TableHead>Maskin</TableHead>
                  <TableHead>Kunde</TableHead>
                  <TableHead>Ordre / faktura</TableHead>
                  <TableHead>Utført av</TableHead>
                  <TableHead className="w-40"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id} data-testid={`report-row-${r.id}`} className="row-hover cursor-pointer" onClick={() => openDetail(r)}>
                    <TableCell>
                      <div className="font-semibold">{r.report_number}</div>
                      <div className="max-w-[260px] truncate text-xs text-muted-foreground">{titleOf(r)}</div>
                    </TableCell>
                    <TableCell><ReportTypeBadge type={r.report_type} /></TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">{formatDate(r.date)}</TableCell>
                    <TableCell className="text-muted-foreground">{machineOf(r) || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{r.customer_snapshot?.name || "—"}{r.email_sent_count > 0 && <div className="mt-1"><EmailSentBadge count={r.email_sent_count} compact /></div>}</TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start gap-1">
                        {r.order_number ? <span className="text-sm tabular-nums" data-testid={`report-order-${r.id}`}>Ordre {r.order_number}</span> : <span className="text-xs text-muted-foreground">Utan ordrenr</span>}
                        <InvoiceBadge invoiced={r.invoiced} compact />
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{r.technician || "—"}{r.technician_title ? <div className="text-xs">{r.technician_title}</div> : null}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => setPreviewReport(r)} data-testid={`preview-report-${r.id}`}><Eye /> Førehandsvis</Button>
                        <Button size="iconSm" variant="ghost" onClick={() => openPdf(r)} data-testid={`open-report-${r.id}`} aria-label="Opne PDF i ny fane"><ExternalLink className="h-4 w-4" /></Button>
                        <Button size="iconSm" variant="ghost" onClick={() => dl(r)} data-testid={`download-report-${r.id}`} aria-label="Last ned"><Download className="h-4 w-4" /></Button>
                        {isAdmin && <Button size="iconSm" variant="ghost" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setToDelete(r)} data-testid={`delete-report-${r.id}`} aria-label="Slett"><Trash2 className="h-4 w-4" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detail || detailLoading} onOpenChange={(o) => !o && setDetail(null)} snapPoints={[0.62, 0.94]}>
        <DialogContent className="flex max-h-[92vh] flex-col gap-0 p-0 sm:max-w-2xl" data-testid="report-detail-dialog">
          {detailLoading || !detail ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <>
              <DialogHeader className="md:border-b md:px-6 md:pb-4 md:pt-6">
                <div className="flex flex-wrap items-center gap-2">
                  <DialogTitle className="font-heading text-xl font-bold">{detail.report_number}</DialogTitle>
                  <ReportTypeBadge type={detail.report_type} />
                  <InvoiceBadge invoiced={detail.invoiced} />
                  <EmailSentBadge count={detail.email_sent_count} />
                </div>
                <DialogDescription>
                  {formatDate(detail.date)} · {detail.technician}{detail.technician_title ? ` (${detail.technician_title})` : ""}{detail.order_number ? ` · Ordrenr ${detail.order_number}` : ""}
                </DialogDescription>
              </DialogHeader>

              <DialogBody className="md:min-h-0 md:flex-1 md:overflow-y-auto md:px-6 md:py-4">
              <div className="grid gap-x-6 sm:grid-cols-2">
                <div className="divide-y divide-border">
                  <Row label="Kunde" value={detail.customer_snapshot?.name} />
                  <Row label="Kontaktperson" value={detail.customer_snapshot?.contact_person} />
                  <Row label={detail.machine_snapshot?.free_text ? "Maskin (ikkje registrert)" : "Maskin"} value={machineOf(detail)} />
                  <Row label="Serienr" value={detail.machine_snapshot?.serial_number} />
                </div>
                <div className="divide-y divide-border">
                  <Row label="Servicetype" value={detail.service_type} />
                  <Row label="Timetal" value={detail.machine_hours != null ? `${num(detail.machine_hours)} t` : null} />
                  <Row label="Tittel" value={detail.title} />
                  <Row label="Timar brukt" value={detail.hours_spent != null ? `${detail.hours_spent} t` : null} />
                  <Row label="Stad" value={detail.location} />
                </div>
              </div>

              <InvoicePanel report={detail} onChange={applyUpdated} testid="report-invoice-panel" />

              <EmailHistory reportId={detail.id} refreshKey={emailRefresh} onSend={() => setSendFor(detail)} testid="report-email-history" />

              {detail.work_performed?.length > 0 && (
                <div className="rounded-lg border p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Utført arbeid</div>
                  <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm">{detail.work_performed.map((w, i) => <li key={i}>{w}</li>)}</ul>
                </div>
              )}
              {detail.description && <div className="rounded-lg border p-3"><Row label="Skildring" value={detail.description} /></div>}
              {detail.defects && <div className="rounded-lg border border-primary/40 bg-primary/5 p-3"><Row label="Avvik / merknader" value={detail.defects} /></div>}
              {(detail.next_service_date || detail.next_service_hours) && (
                <p className="text-sm text-muted-foreground">Anbefalt neste service: {[detail.next_service_hours && `${num(detail.next_service_hours)} t`, detail.next_service_date && formatDate(detail.next_service_date)].filter(Boolean).join(" eller ")}</p>
              )}

              {detail.images?.length > 0 && (
                <div>
                  <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"><Images className="h-3.5 w-3.5" /> {detail.images.length} bilete</div>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="report-detail-images">
                    {detail.images.map((img, i) => (
                      <button key={i} type="button" onClick={() => openImage(img)} className="group overflow-hidden rounded-lg border bg-muted text-left" data-testid={`report-detail-image-${i}`} aria-label={`Opne bilete ${i + 1}`}>
                        <img src={imageSrc(img, { size: "thumb" })} alt={imageCaption(img) || `Bilete ${i + 1}`} className="aspect-[4/3] w-full object-cover transition-transform duration-150 group-hover:scale-[1.03]" loading="lazy" />
                        {imageCaption(img) && <div className="truncate px-1.5 py-1 text-[11px] text-muted-foreground">{imageCaption(img)}</div>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {detail.voice_note?.file_id && (
                <div className="rounded-xl border bg-muted/40 p-3" data-testid="report-detail-voice">
                  <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <FileAudio className="h-3.5 w-3.5" /> Lydnotat{detail.voice_note.duration_s ? ` · ${formatClock(detail.voice_note.duration_s)}` : ""}
                  </div>
                  <audio controls preload="none" src={fileUrl(detail.voice_note.file_id)} className="h-10 w-full" data-testid="report-detail-voice-player" />
                  {detail.voice_note.transcript && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-xs font-semibold text-muted-foreground hover:text-foreground">Vis transkripsjon</summary>
                      <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground" data-testid="report-detail-voice-transcript">{detail.voice_note.transcript}</p>
                    </details>
                  )}
                </div>
              )}

              </DialogBody>
              <DialogFooter className="grid grid-cols-2 gap-2 md:flex md:border-t md:px-6 md:py-3 sm:gap-0">
                {isAdmin && <Button variant="ghost" className="order-last col-span-2 text-destructive hover:bg-destructive/10 hover:text-destructive md:order-none md:col-span-1 md:mr-auto" onClick={() => setToDelete(detail)} data-testid="detail-delete-report"><Trash2 /> Slett</Button>}
                <Button variant="outline" onClick={() => dl(detail)} data-testid="detail-download-pdf"><Download /> Last ned</Button>
                <Button variant="outline" onClick={() => setPreviewReport(detail)} data-testid="detail-preview-pdf"><Eye /> Førehandsvis</Button>
                <Button className="order-first col-span-2 md:order-none md:col-span-1" onClick={() => setSendFor(detail)} data-testid="detail-send-email"><Send /> Send til kunde</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <PdfPreviewDialog open={!!previewReport} onOpenChange={(o) => !o && setPreviewReport(null)} report={previewReport} />

      <SendReportDialog report={sendFor} open={!!sendFor} onOpenChange={(o) => !o && setSendFor(null)} onSent={onEmailSent} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slette rapport?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.report_number} blir sletta permanent.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-report">Slett</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
